// Local-scope state and declaration extraction for function expansion.
// Handles `$name: value;` assignments and typed value inference.
import { splitTopLevelStatements } from '../../variables/class-variable/transform/lines'
import { isArrayVariableValue, parseArrayVariable, serializeArrayVariable } from '../../variables/array-variable'
import { isObjectVariableValue, parseObjectVariable } from '../../variables/object-variable'
import { isValidFunctionIdentifier } from '../helpers'
import type { LuisFunctionLocalScope } from '../types'
import { replaceLocalReferences } from './references'
import type { LocalDeclaration, LocalExtractionResult } from './scope-types'
// Strip wrapping quotes from plain scalar argument values.
import { stripWrappingQuotes } from '../../loops/for-loop/selector'

// Create an empty local scope container.
export function createEmptyLocalScope(): LuisFunctionLocalScope {
  // Return empty local maps for values/objects/arrays.
  return { values: {}, objects: {}, arrays: {} }
}

// Build local scope parameter bindings from ordered function arguments.
export function assignFunctionParams(scope: LuisFunctionLocalScope, paramNames: string[], args: string[]): void {
  // Assign each parameter from the corresponding argument value.
  for (let index = 0; index < paramNames.length; index += 1) {
    const paramName = paramNames[index]
    const argValue = args[index] ?? ''
    assignLocalValue(scope, paramName, argValue)
  }
}

// Build body source without top-level local declarations.
/** @internal Build body source without top-level local declarations. */
export function extractLocalDeclarations(source: string, inherited: LuisFunctionLocalScope): LocalExtractionResult {
  const scope = cloneLocalScope(inherited)
  const outputParts: string[] = []
  const parts = splitTopLevelStatements(source)

  // Scan top-level statements and strip local declarations before later expansion.
  for (const part of parts) {
    const trimmed = part.text.trim()
    const localDeclaration = parseLocalDeclaration(trimmed)
    if (part.hasTerminator && localDeclaration) {
      const resolvedValue = replaceLocalReferences(localDeclaration._value, scope)
      assignLocalValue(scope, localDeclaration._name, resolvedValue)
      continue
    } else {
      // Keep non-local statements untouched.
    }

    const suffix = part.hasTerminator ? ';' : ''
    outputParts.push(`${part.text}${suffix}`)
  }

  // Return body text without local declarations and merged local scope.
  return {
    _source: outputParts.join(''),
    _scope: scope,
  }
}

// Resolve one `$name: value` declaration from trimmed source.
function parseLocalDeclaration(input: string): LocalDeclaration | null {
  const separator = input.indexOf(':')
  // Check whether the statement starts with a valid local declaration prefix.
  if (separator <= 1 || input[0] !== '$') {
    // Return null for non-local declaration statements.
    return null
  } else {
    // Continue local declaration name/value parsing.
  }

  const name = input.slice(1, separator).trim()
  const value = input.slice(separator + 1).trim()
  // Check whether the parsed declaration has a valid name and value.
  if (!isValidFunctionIdentifier(name) || !value) {
    // Return null for invalid local declaration payloads.
    return null
  } else {
    // Return parsed local declaration payload.
    return { _name: name, _value: value }
  }
}

// Build one local value assignment and infer object or array maps when needed.
function assignLocalValue(scope: LuisFunctionLocalScope, name: string, value: string): void {
  const trimmed = value.trim()
  // Check whether the local value should be stored as an object.
  if (isObjectVariableValue(trimmed)) {
    const parsedObject = parseObjectVariable(trimmed)
    scope.objects[name] = parsedObject
    scope.values[name] = serializeObjectValue(parsedObject)
  } else if (isArrayVariableValue(trimmed)) {
    // Continue typed storage for array-shaped local values.
    const parsedArray = parseArrayVariable(trimmed)
    scope.arrays[name] = parsedArray
    scope.values[name] = serializeArrayVariable(parsedArray)
  } else {
    // Strip wrapping quotes from plain scalar values for direct `$name` substitution.
    scope.values[name] = stripWrappingQuotes(trimmed)
  }
}

// Serialize object variable map back into `{ key: value; }` text.
function serializeObjectValue(value: Record<string, string>): string {
  const entries = Object.entries(value).map(([key, entryValue]) => `${key}: ${entryValue};`)
  // Return compact object text representation for `$obj` substitutions.
  return `{ ${entries.join(' ')} }`
}

// Create a cloned local scope object for isolated per-call mutation.
function cloneLocalScope(scope: LuisFunctionLocalScope): LuisFunctionLocalScope {
  // Return a cloned local scope with copied nested object and array maps.
  return {
    values: { ...scope.values },
    objects: Object.fromEntries(Object.entries(scope.objects).map(([key, value]) => [key, { ...value }])),
    arrays: Object.fromEntries(Object.entries(scope.arrays).map(([key, value]) => [key, [...value]])),
  }
}
